interface CircularTimerProps {
  image: string;
  totalDuration: number;
  remainingTime: number;
  mode: 'cover' | 'uncover';
  size?: number;
  timerId?: string | number;
}

export default function CircularTimer({ 
  image, 
  totalDuration, 
  remainingTime, 
  mode, 
  size = 200,
  timerId
}: CircularTimerProps) {
  // Use props directly - no store dependency
  // This ensures care view countdown derives purely from server data
  const currentRemainingTime = remainingTime ?? totalDuration ?? 0;
  const currentTotalDuration = totalDuration || 300;
  
  // Calculate elapsed progress (0 to 1, where 1 = timer finished)
  const elapsedProgress = currentTotalDuration > 0 
    ? Math.max(0, Math.min(1, (currentTotalDuration - currentRemainingTime) / currentTotalDuration))
    : 0;
  
  // Calculate the angle for the circular mask (0 = no overlay, 360 = full overlay)
  const angle = elapsedProgress * 360;
  
  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds) % 60;
    // Return HH:MM:SS if >= 1 hour, otherwise MM:SS
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate deterministic mask ID based on timerId
  const maskId = `progressMask-${timerId || 'default'}`;

  return (
    <div className="relative" style={{ width: size, height: size }} data-testid={`circular-timer-${timerId}`}>
      {/* Base image */}
      <div 
        className="w-full h-full rounded-full overflow-hidden bg-gray-200"
        style={{
          backgroundImage: image ? `url(${image})` : undefined,
          backgroundSize: 'contain',
          backgroundPosition: 'center'
        }}
      >
        {/* Circular progress overlay - only show when timer is running/paused */}
        {elapsedProgress > 0 && (
          <svg 
            className="absolute inset-0 w-full h-full" 
            viewBox="0 0 100 100"
          >
            <defs>
              <mask id={maskId}>
                <rect width="100" height="100" fill="black" />
                <path
                  d={`M 50,50 L 50,0 A 50,50 0 ${angle > 180 ? 1 : 0},1 ${
                    50 + 50 * Math.sin((angle * Math.PI) / 180)
                  },${50 - 50 * Math.cos((angle * Math.PI) / 180)} Z`}
                  fill="white"
                />
              </mask>
            </defs>
            
            <circle
              cx="50"
              cy="50"
              r="50"
              fill="#6b7280"
              mask={`url(#${maskId})`}
              opacity="0.7"
            />
          </svg>
        )}
      </div>
      
      {/* Timer display */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white bg-opacity-90 px-3 py-1 rounded-full font-bold text-lg shadow-lg">
          {formatTime(currentRemainingTime)}
        </div>
      </div>
      
      {/* Progress indicator ring */}
      <svg 
        className="absolute inset-0 w-full h-full" 
        viewBox="0 0 100 100"
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="2"
        />
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="none"
          stroke={mode === 'cover' ? '#ef4444' : '#10b981'}
          strokeWidth="3"
          strokeDasharray={`${elapsedProgress * 301.59} 301.59`}
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}
